import React, { useMemo } from 'react';
import { observer } from 'mobx-react';
import { IUICfgUICompositeProductInfoElementUI } from '@canvas-logic/clp-api-types';
import { RootContainerCfgUIElement } from '@/elements/containers/RootContainer/RootContainer';
import { ComponentItemsContainerCfgUIElement } from '@/elements/containers/ComponentItemsContainer/ComponentItemsContainer';
import {
  createMockTextAttributeChild,
  createMockComponentChild,
  createMockContainerChild,
  createMockCompositeProductInfoChild,
  MockTextAttributeConfig,
} from '../mocks';
import { MockComponentConfig } from '../mocks/MockContainerChildren';
import { MockCompositeProductInfoConfig } from '../mocks/MockComponentStates';
import { PRODUCT_IMAGES, mockRootContainer, mockComponentItemsContainer } from '../mockData';
import styles from './StyleGuideSidebar.module.css';

// ============================================================================
// Figma-matching mock data — text, images, and card counts from node 1306:26264
// ============================================================================

const DESCRIPTION = 'Organize your coffee cups with ease using our innovative cup storage rack.';
const IMG = PRODUCT_IMAGES.badgeScanner;

/** Composite product info heading: "Ceres Flex" — the product name at top of each sidebar */
const ceresFlexInfo: MockCompositeProductInfoConfig = {
  viewElement: {
    id: 'sidebar-ceres-flex',
    kind: 'ui-element',
    type: 'composite-product-info',
    name: 'Ceres Flex',
    enabled: true,
    showTitle: true,
    showDescription: false,
    showShortDescription: false,
    showImage: false,
  } as IUICfgUICompositeProductInfoElementUI,
  productData: {
    productKey: 'ceres-flex',
    texts: {
      name: 'Ceres Flex',
      description: '',
    },
  },
};

/** Component with toggle switch — "Option name" with title, description, and switcher */
function makeComponent(id: string): MockComponentConfig {
  return {
    viewElement: {
      id,
      kind: 'component',
      type: 'default',
      name: 'Option name',
      enabled: true,
      source: { type: 'component', componentKey: id },
      uiControl: { showTitle: true, showDescription: true, showSwitcher: true, showComponent: 'always' },
    } as any,
    lookupConfig: {
      componentKey: id,
      isOptional: true,
      isEnabled: true,
      name: 'Option name',
      description: DESCRIPTION,
    },
  };
}

/** Card grid attribute — N cards with product image */
function makeCardGrid(id: string, attrKey: string, numValues: number): MockTextAttributeConfig {
  return {
    id,
    initialValue: 'v1',
    availableValues: Array.from({ length: numValues }, (_, i) => `v${i + 1}`),
    viewElement: {
      id,
      kind: 'attribute',
      type: 'text',
      name: 'Option name',
      enabled: true,
      source: { type: 'attribute', componentKey: 'ceres-flex', attributeKey: attrKey },
      uiControl: {
        cardType: 'grid',
        showTitle: true,
        showDescription: true,
        itemOptions: { showTitle: false, showImage: true, showDescription: false },
      },
    } as any,
    textData: {
      name: 'Option name',
      description: DESCRIPTION,
      values: Array.from({ length: numValues }, (_, i) => ({
        value: `v${i + 1}`,
        label: `Option ${i + 1}`,
        image: { url: IMG },
      })),
    } as any,
  };
}

// ============================================================================
// Sidebar size variants — matching Figma labels and widths exactly
// ============================================================================

/**
 * Six sidebar variants from Figma (right side: "Cards on a different Sidebar sizes").
 * Order matches Figma left-to-right: min 3, min 2, min 4, med 3, med 4, max 4.
 */
const SIDEBAR_SIZES = [
  { label: 'min 3', width: 244 },
  { label: 'min 2', width: 244 },
  { label: 'min 4', width: 327 },
  { label: 'med 3', width: 328 },
  { label: 'med 4', width: 427 },
  { label: 'max 4', width: 428 },
];

/**
 * Spacing tokens from the Figma bottom-left reference.
 * Three value columns match Figma: XS/SM, MD, LG.
 */
const SPACING_TOKENS = [
  {
    token: '--spacing-xxl',
    figmaName: 'spacing-xxl',
    xs: '40',
    md: '60',
    lg: '80',
    usage: 'Top/bottom section padding',
  },
  {
    token: '--gap-section',
    figmaName: 'gap-section',
    xs: '40',
    md: '60',
    lg: '80',
    usage: 'Gap between 1st-level sections (Level 1)',
  },
  {
    token: '--gap-subsection',
    figmaName: 'gap-subsection',
    xs: '24',
    md: '36',
    lg: '48',
    usage: 'Gap between 2nd-level items (Level 2)',
  },
  {
    token: '--gap-section-subsection',
    figmaName: 'gap-section+subsection',
    xs: '8',
    md: '12',
    lg: '16',
    usage: 'Gap within component + attributes (Level 3)',
  },
  {
    token: '--padding-screen',
    figmaName: 'padding-screen',
    xs: '24',
    md: '40',
    lg: '80',
    usage: 'Sidebar horizontal padding',
  },
];

/**
 * Sidebar style guide page — mirrors Figma "Components Sidebar" (node-id: 1306:26264).
 *
 * Shows:
 * 1. Four sidebar width variants (min 3 / min 2 / med 3 / max 4)
 * 2. Default spacing tokens table (overridable via ui-config)
 */
export const StyleGuideSidebar: React.FC = observer(function StyleGuideSidebar() {
  // ============================================================================
  // Mock sidebar content tree — matches Figma sidebar structure:
  //   Heading (Ceres Flex composite-product-info) → Component (with switch) →
  //   Section (component + 4-card grid) → Section (component + 3 sub-grids: 4, 4, 8 cards) →
  //   Component (with switch)
  // ============================================================================

  // Heading: "Ceres Flex" composite product name (cp-composite-product-info)
  const heading = useMemo(() => createMockCompositeProductInfoChild(ceresFlexInfo), []);

  // Standalone component with switch at top: "Option name"
  const compTop = useMemo(() => createMockComponentChild(makeComponent('comp-top')), []);

  // Section 1: component header (with switch) + card grid (4 cards)
  const sec1Comp = useMemo(() => createMockComponentChild(makeComponent('sec1-comp')), []);
  const sec1Grid = useMemo(() => createMockTextAttributeChild(makeCardGrid('sec1-grid', 'sec1-attr', 4)), []);
  const sec1Children = useMemo(() => [sec1Comp, sec1Grid], [sec1Comp, sec1Grid]);

  // Section 2: component header (with switch) + 3 sub-grids (4 + 4 + 8 cards)
  const sec2Comp = useMemo(() => createMockComponentChild(makeComponent('sec2-comp')), []);
  const sec2Grid1 = useMemo(() => createMockTextAttributeChild(makeCardGrid('sec2-grid1', 'sec2-attr1', 4)), []);
  const sec2Grid2 = useMemo(() => createMockTextAttributeChild(makeCardGrid('sec2-grid2', 'sec2-attr2', 4)), []);
  const sec2Grid3 = useMemo(() => createMockTextAttributeChild(makeCardGrid('sec2-grid3', 'sec2-attr3', 8)), []);
  const sec2Children = useMemo(
    () => [sec2Comp, sec2Grid1, sec2Grid2, sec2Grid3],
    [sec2Comp, sec2Grid1, sec2Grid2, sec2Grid3],
  );

  // Standalone component with switch at bottom: "Option name"
  const compBottom = useMemo(() => createMockComponentChild(makeComponent('comp-bottom')), []);

  // Full sidebar tree
  const sidebarChildren = useMemo(
    () => [
      heading,
      compTop,
      createMockContainerChild(
        ComponentItemsContainerCfgUIElement,
        { ...mockComponentItemsContainer, id: 'sidebar-sec-1', expandable: false, defaultExpanded: true },
        sec1Children,
        { level: 2, parentLevel: 1 },
      ),
      createMockContainerChild(
        ComponentItemsContainerCfgUIElement,
        { ...mockComponentItemsContainer, id: 'sidebar-sec-2', expandable: false, defaultExpanded: true },
        sec2Children,
        { level: 2, parentLevel: 1 },
      ),
      compBottom,
    ],
    [heading, compTop, sec1Children, sec2Children, compBottom],
  );

  return (
    <div className={styles.section}>
      {/* ================================================================
       * SECTION: Sidebar Sizes
       * Mirrors Figma right side: "Cards on a different Sidebar sizes"
       * ================================================================ */}
      <div className={styles.sectionHeader}>
        <h2 className={styles.sectionTitle}>Cards on a different Sidebar sizes</h2>
      </div>

      <div className={styles.cardDemo}>
        <h3 className={styles.cardDemoTitle}>layout.sidebarMaxWidth</h3>
        <div className={styles.sizesRow}>
          {SIDEBAR_SIZES.map(({ label, width }, i) => (
            <div key={`${label}-${i}`} className={styles.sidebarPanel} style={{ width }}>
              <div className={styles.sidebarPanelHeader}>
                <span className={styles.sidebarPanelLabel}>{label}</span>
                <span className={styles.sidebarPanelWidth}>{width}px</span>
              </div>
              <div className={styles.sidebarContent}>
                <RootContainerCfgUIElement viewElement={mockRootContainer} childrenItems={sidebarChildren} />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ================================================================
       * SECTION: Default Spacing
       * Mirrors Figma left side: "Default Sidebar spacing between blocks"
       * ================================================================ */}
      <div className={styles.sectionHeader}>
        <h2 className={styles.sectionTitle}>Default Sidebar spacing between blocks</h2>
      </div>

      <div className={styles.cardDemo}>
        <h3 className={styles.cardDemoTitle}>Spacing Tokens</h3>
        <p className={styles.note}>
          Default sidebar spacing between blocks. These values may be overridden by configurator settings (ui-config).
        </p>
        <table className={styles.tokenTable}>
          <thead>
            <tr>
              <th>Token</th>
              <th>Figma name</th>
              <th>XS / SM</th>
              <th>MD</th>
              <th>LG</th>
              <th>Usage</th>
            </tr>
          </thead>
          <tbody>
            {SPACING_TOKENS.map(({ token, figmaName, xs, md, lg, usage }) => (
              <tr key={token}>
                <td>
                  <code className={styles.code}>{token}</code>
                </td>
                <td>
                  <code className={styles.figmaToken}>{figmaName}</code>
                </td>
                <td>{xs}px</td>
                <td>{md}px</td>
                <td>{lg}px</td>
                <td>{usage}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
});
